// src/components/BookNowCTA.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PriceTag from '../components/PriceTag';
import BokunBookButton from '../components/BokunBookButton';
import { BOKUN_EXPERIENCE } from '../constants/booking';

const CTASection = styled.section`
    padding: 3rem 0;
    background: #f3d717;
    position: relative;
    overflow: hidden;
`;

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 2;
`;

const CTAContent = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 3rem;
    align-items: center;
    background: white;
    border-radius: 20px;
    padding: 3rem;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.8);
    position: relative;
    overflow: hidden;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 2rem;
        padding: 2rem;
        text-align: center;
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-radius: 20px 20px 0 0;
    }
`;

const TextContent = styled.div`
    h2 {
        font-size: 2.5rem;
        font-weight: 800;
        margin-bottom: 1rem;
        background: linear-gradient(135deg, #1a1a1a 0%, #667eea 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        line-height: 1.2;

        @media (max-width: 768px) {
            font-size: 2rem;
        }
    }

    p {
        font-size: 1.2rem;
        color: #4a5568;
        line-height: 1.6;
        margin-bottom: 1.5rem;

        @media (max-width: 768px) {
            font-size: 1.1rem;
        }
    }
`;

const FeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-top: 2rem;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const FeatureItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #4a5568;
    font-size: 0.95rem;

    @media (max-width: 768px) {
        justify-content: center;
    }
`;

const FeatureIcon = styled.span`
    color: #10b981;
    font-size: 1.1rem;
    flex-shrink: 0;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    justify-content: center;
    width: 100%;

    @media (max-width: 768px) {
        align-items: center;
    }
`;

const PrimaryButton = styled(BokunBookButton)`
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    font-family: inherit;
    box-sizing: border-box;
    text-decoration: none;
    padding: 1.25rem 2.5rem;
    border-radius: 12px;
    font-size: 1.2rem;
    font-weight: 600;
    text-align: center;
    width: 80%;
    max-width: 280px;
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
    transition: all 0.3s ease;
    border: none;
    cursor: pointer;
    display: block;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 35px rgba(102, 126, 234, 0.4);
    }

    @media (max-width: 768px) {
        max-width: 100%;
        padding: 1.25rem 2rem;
    }
`;

const SecondaryButton = styled(motion(Link))`
    background: transparent;
    color: #667eea;
    text-decoration: none;
    padding: 1rem 2rem;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 500;
    text-align: center;
    width: 80%;
    max-width: 280px;
    border: 2px solid #667eea;
    transition: all 0.3s ease;
    cursor: pointer;
    display: block;

    &:hover {
        background: rgba(102, 126, 234, 0.1);
        transform: translateY(-2px);
    }

    @media (max-width: 768px) {
        max-width: 100%;
        padding: 1rem 2rem;
    }
`;

const PriceContainer = styled.div`
    text-align: center;
    margin-bottom: 1.5rem;
    width: 100%;
`;

const PriceNote = styled.div`
    color: #718096;
    font-size: 0.9rem;
`;

const FloatingElements = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
`;

const FloatingElement = styled(motion.div)`
    position: absolute;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 15px;
    opacity: 0.1;

    &.element-1 {
        width: 80px;
        height: 80px;
        top: 20%;
        right: 10%;
    }

    &.element-2 {
        width: 60px;
        height: 60px;
        bottom: 30%;
        left: 5%;
    }
`;

interface BookNowCTAProps {
    price?: string;
    originalPrice?: string;
    showPrice?: boolean;
}

const BookNowCTA: React.FC<BookNowCTAProps> = ({
                                                   price = "119€",
                                                   originalPrice = "169€",
                                                   showPrice = true
                                               }) => {
    return (
        <CTASection>
            <FloatingElements>
                <FloatingElement
                    className="element-1"
                    animate={{
                        y: [0, -20, 0],
                        rotate: [0, 90, 180]
                    }}
                    transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />
                <FloatingElement
                    className="element-2"
                    animate={{
                        y: [0, 15, 0],
                        rotate: [0, -45, 0]
                    }}
                    transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1
                    }}
                />
            </FloatingElements>

            <Container>
                <CTAContent>
                    <TextContent>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            Ready for Your Adventure?
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            viewport={{ once: true }}
                        >
                            Don't miss this unforgettable journey through Slovakia's castles and traditional villages.
                            Book now to secure your spot and create memories that will last a lifetime.
                        </motion.p>

                        <FeaturesGrid>
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.4, delay: 0.2 }}
                                viewport={{ once: true }}
                            >
                                <FeatureItem>
                                    <FeatureIcon>✓</FeatureIcon>
                                    <span>Free Cancellation</span>
                                </FeatureItem>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.4, delay: 0.3 }}
                                viewport={{ once: true }}
                            >
                                <FeatureItem>
                                    <FeatureIcon>✓</FeatureIcon>
                                    <span>Instant Confirmation</span>
                                </FeatureItem>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.4, delay: 0.4 }}
                                viewport={{ once: true }}
                            >
                                <FeatureItem>
                                    <FeatureIcon>✓</FeatureIcon>
                                    <span>Best Price Guarantee</span>
                                </FeatureItem>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.4, delay: 0.5 }}
                                viewport={{ once: true }}
                            >
                                <FeatureItem>
                                    <FeatureIcon>✓</FeatureIcon>
                                    <span>24/7 Support</span>
                                </FeatureItem>
                            </motion.div>
                        </FeaturesGrid>
                    </TextContent>

                    <ButtonContainer>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            viewport={{ once: true }}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                width: '100%',
                                gap: '1rem'
                            }}
                        >
                            {showPrice && (
                                <PriceContainer>
                                    <PriceTag
                                        prefix="From "
                                        originalPrice={originalPrice}
                                        price={price}
                                        size="lg"
                                        align="center"
                                    />
                                    <PriceNote>per person</PriceNote>
                                </PriceContainer>
                            )}

                            <PrimaryButton experienceId={BOKUN_EXPERIENCE.slovakia}>
                                Book Now
                            </PrimaryButton>

                            <SecondaryButton
                                to="/contact"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                Contact Us
                            </SecondaryButton>
                        </motion.div>
                    </ButtonContainer>
                </CTAContent>
            </Container>
        </CTASection>
    );
};

export default BookNowCTA;